// src/utils/ensureArray.js

/**
 * Normalize any value into an array for safe iteration.
 * - Arrays → same array
 * - null/undefined → []
 * - string → [string]  (so a single id/name can still render)
 * - Map/Set/iterables → [...iterable]
 * - plain objects → Object.values(obj)
 * - numbers/booleans/etc. → [value]
 */
export function ensureArray(value) {
  if (Array.isArray(value)) return value;
  if (value == null) return [];

  if (typeof value === "string") return [value];

  // Common iterables
  if (value instanceof Map) return Array.from(value.values());
  if (value instanceof Set) return Array.from(value);

  if (typeof value?.[Symbol.iterator] === "function") return Array.from(value);

  // Plain object from APIs (keyed by id)
  if (typeof value === "object") return Object.values(value);

  // numbers/booleans/etc.
  return [value];
}

/**
 * Map over a value as if it were always an array.
 * Prevents ".map is not a function" by normalizing first.
 */
export function safeMap(value, fn) {
  return ensureArray(value).map(fn);
}

/**
 * Returns a guaranteed array (possibly empty).
 * Useful when you only want [] or a real array (no singletons).
 */
export function nonEmptyArray(value) {
  const arr = ensureArray(value);
  return Array.isArray(arr) ? arr : [];
}

export default ensureArray;
